import { Box, Chip, Tooltip } from "@mui/material";
import React, { FC } from "react";

import { getPlayersWithRegistrations } from "../../../features/player/playerSlice";
import { useAppSelector } from "../../../store";
import { Players } from "../../../types";
import BasePlayerSelector from "../../Base/BasePlayerSelector";
import { selectMatchesWithBlockInfo } from "../../../features/matchs/matchsSlice";

type Props = {
  label: string;
  currentPlayerId: string | null;
  onPlayerSelect: (player: Players | null) => void;
  currentBoardId?: string;
  showAllPlayers?: boolean;
};

const MatchPlayerSelector: FC<Props> = ({
  label,
  onPlayerSelect,
  currentPlayerId,
  currentBoardId,
  showAllPlayers = false,
}) => {
  const players = useAppSelector(getPlayersWithRegistrations);
  const matches = useAppSelector(selectMatchesWithBlockInfo);

  const filteredPlayers = showAllPlayers
    ? players.filter((p) => (p.participation?.length ?? 0) > 0)
    : currentBoardId
    ? players.filter((p) =>
        p.participation?.some((part) => part.tableauId === currentBoardId)
      )
    : [];

  const isPlayerInMatch = (playerId: string) => {
    return matches.some(
      (match) =>
        match.status === "ENCOURS" &&
        match.matchsPlayerParticipations.some(
          (mpp) => mpp.playersId === playerId
        )
    );
  };

  const isPlayerRegisteredInBoard = (player: Players) => {
    if (!currentBoardId) return true;
    return (
      player.participation?.some((p) => p.tableauId === currentBoardId) ?? false
    );
  };

  const getPlayerStatus = (player: Players) => {
    if (isPlayerInMatch(player.id)) {
      return {
        label: "En match",
        color: "error" as const,
        tooltip: "Ce joueur est actuellement dans un match en cours",
      };
    }
    if (!isPlayerRegisteredInBoard(player)) {
      return {
        label: "Non inscrit",
        color: "warning" as const,
        tooltip: "Ce joueur n'est pas inscrit à ce tableau",
      };
    }
    return null;
  };

  const getPlayerMatchInfo = (playerId: string) => {
    const match = matches.find(
      (match) =>
        match.status === "ENCOURS" &&
        match.matchsPlayerParticipations.some(
          (mpp) => mpp.playersId === playerId
        )
    );
    if (match) {
      return {
        tableId: match.tableId,
        duration: Math.floor(
          (Date.now() - match.dateLancement!.getTime()) / 1000 / 60
        ),
      };
    }
    return null;
  };

  const renderPlayerOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Players
  ) => {
    const status = getPlayerStatus(option);
    const matchInfo = isPlayerInMatch(option.id)
      ? getPlayerMatchInfo(option.id)
      : null;
    return (
      <li {...props}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span>
            {option.name} {option.surname}
          </span>
          {status && (
            <Tooltip title={status.tooltip}>
              <Chip
                label={status.label}
                color={status.color}
                size="small"
                sx={{ ml: 1 }}
              />
            </Tooltip>
          )}
          {matchInfo && (
            <Tooltip
              title={`En match sur la table ${matchInfo.tableId} depuis ${matchInfo.duration} minutes`}
            >
              <Chip
                label={`Table ${matchInfo.tableId}`}
                color="warning"
                size="small"
                sx={{ ml: 1 }}
              />
            </Tooltip>
          )}
        </Box>
      </li>
    );
  };

  return (
    <BasePlayerSelector
      label={label}
      currentPlayerId={currentPlayerId}
      onPlayerSelect={onPlayerSelect}
      players={filteredPlayers}
      renderOption={renderPlayerOption}
      getOptionLabel={(option) => `${option.name} ${option.surname}`}
      sx={{ width: "100%" }}
    />
  );
};

export default MatchPlayerSelector;
